import {useTranslate, IResourceComponentsProps, useShow, useRouterContext, useNavigation, useApiUrl} from "@pankod/refine-core";
import {
  Edit,
  Form,
  Input,
  Select,
  useSelect,
  useForm,
  DeleteButton,
  message,
} from "@pankod/refine-antd";

import "react-mde/lib/styles/css/react-mde-all.css";

import { IEvent, ILanguage, IQuiz, IQuizCategory } from "interfaces";
import RelatedQuizTags from "./related";
import SyncChildrenButton from "./syncChildrenButton";
import KeywordTagsSelect from "components/keywordTagsSelect";

const { TextArea } = Input;

export const QuizEdit: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate();
  const { useParams } = useRouterContext();
  const { id } = useParams<{ id: string }>();
  const { edit, list } = useNavigation();
  const apiUrl = useApiUrl();

  const { formProps, saveButtonProps, queryResult } = useForm<IQuiz>({
    resource: "quiz",
    id,
    // optional but helps ensure refetch:
    queryOptions: { enabled: !!id },
  });

  const record = queryResult?.data?.data;

  const { selectProps: quizCategorySelectProps } = useSelect<IQuizCategory>({
    resource: "quiz-category",
    optionLabel: "name",
    optionValue: "quiz_category_id",
  });

  const { selectProps: languageSelectProps } = useSelect<ILanguage>({
    resource: "language",
    optionLabel: "name",
    optionValue: "lang_code",
  });

  const { selectProps: eventSelectProps } = useSelect<IEvent>({
    resource: "event",
    optionLabel: "name",
    optionValue: "event_id",
    defaultValue: record?.event_id,
  });

  return (
    <Edit saveButtonProps={saveButtonProps} footerButtons={({ defaultButtons }) => (
        <>
          {defaultButtons}
          <SyncChildrenButton
            quizId={id}
            parentId={record?.parent_id}
            apiUrl={apiUrl}
          />
          <DeleteButton
            recordItemId={id}
            resource="quiz"
            confirmTitle={
              !record?.parent_id
                ? t("quiz.messages.delete_parent_warning")
                : undefined
            }
            onSuccess={() => {
              if (record?.parent_id) {
                edit("quiz", record.parent_id);
                window.scrollTo({ top: 0, behavior: "smooth" });
                message.success(t("quiz.messages.redirected_to_parent"));
              } else {
                list("quiz");
              }
            }}
          />
        </>
      )}>
      <Form {...formProps} layout="vertical">
        <Form.Item label={t("quiz.fields.languages")}>
          <RelatedQuizTags quiz={record} />
        </Form.Item>
        <Form.Item label={t("quiz.fields.parent_id")} name="parent_id" rules={[{ required: false }]}>
          <Input />
        </Form.Item>
        <Form.Item label={t("quiz.fields.question")} name="question" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label={t("quiz.fields.answer1")} name="answer1" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label={t("quiz.fields.answer2")} name="answer2" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label={t("quiz.fields.answer3")} name="answer3" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label={t("quiz.fields.answer4")} name="answer4" rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        <Form.Item label={t("quiz.fields.correct_answer")} name="correct_answer" rules={[{ required: true }]}>
          <Select
            options={[
              { label: 1, value: 1 },
              { label: 2, value: 2 },
              { label: 3, value: 3 },
              { label: 4, value: 4 },
            ]}
          />
        </Form.Item>

        <Form.Item
          label={t("quiz.fields.quiz_category_name")}
          name={["quiz_category_id"]}
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Select {...quizCategorySelectProps} />
        </Form.Item>

        <Form.Item
          label={t("quiz.fields.event_name")}
          name={["event_id"]}
          rules={[{ required: false }]}
        >
          <Select {...eventSelectProps} allowClear />
        </Form.Item>

        <Form.Item
            label={t("quiz.fields.lang")}
            name={["lang"]}
            rules={[
              {
                required: true,
              },
            ]}
        >
          <Select {...languageSelectProps} />
        </Form.Item>

        <Form.Item label={t("quiz.fields.explanation")} name="explanation" rules={[{ required: true }]}>
          <TextArea rows={10} />
        </Form.Item>

        <Form.Item label={t("quiz.fields.keywords")} name="keywords" rules={[{ required: false }]}>
          <KeywordTagsSelect />
        </Form.Item>

        <Form.Item label={t("quiz.fields.level")} name="level" rules={[{ required: true }]}>
          <Select
              options={[
                { label: 1, value: 1 },
                { label: 2, value: 2 },
                { label: 3, value: 3 },
              ]}
          />
        </Form.Item>

        <Form.Item label={t("quiz.fields.status")} name="status" rules={[{ required: true }]}>
          <Select
              options={[
                { label: t("quiz.status.enabled"), value: "enabled" },
                { label: t("quiz.status.disabled"), value: "disabled" },
                { label: t("quiz.status.for_review"), value: "for_review" },
              ]}
          />
        </Form.Item>
      </Form>
    </Edit>
  );
};
